import { z } from 'zod';
import { Info } from 'luxon';
/**
 * IanaTimeZone — a canonical IANA timezone identifier.
 *
 * Validated at the Zod boundary via a uppercase-canonical prefix guard +
 * Luxon's `Info.isValidIANAZone`. This is the single place in the codebase
 * where Luxon participates in validation; the domain kernel
 * (`apps/api/src/shared/kernel/time/iana-time-zone.vo.ts`) remains Luxon-free
 * and trusts this gate (see design §3.4, ADR 0011, ADR 0014).
 *
 * Policy (ADR 0014 — resolves design §13 deferred canonicalization question):
 *   - **Accept canonical IANA names only.** Names must start with an
 *     uppercase Latin letter (matches real IANA zones: `UTC`,
 *     `America/Los_Angeles`, `Europe/Lisbon`, etc.).
 *   - **Reject Luxon-accepted aliases that are NOT canonical IANA:**
 *     lowercase `utc` / `america/los_angeles` are rejected even though
 *     Luxon's `Info.isValidIANAZone` accepts them; offset strings like
 *     `+02:00` are also rejected (Luxon leniently accepts them — they are
 *     offsets, not IANA identifiers). The uppercase-prefix guard runs
 *     BEFORE the Luxon refine so the rejection reason is stable.
 *   - `Asia/Calcutta` (uppercase, historical alias) IS accepted — Luxon
 *     resolves it to `Asia/Kolkata`; this is a deliberate pin of current
 *     Luxon behaviour.
 *
 * Constraints:
 *   - `min(1)` — empty strings rejected.
 *   - `max(64)` — upper bound matches the `VARCHAR(64)` DB column.
 *   - `refine(/^[A-Z]/)` — uppercase-canonical gate (ADR 0014 policy).
 *   - `refine(Info.isValidIANAZone, ...)` — IANA-valid per the installed
 *     Luxon tzdata.
 */
export const IanaTimeZoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine((value) => /^[A-Z]/.test(value), 'must start with an uppercase letter (canonical IANA)')
    .refine((value) => Info.isValidIANAZone(value), 'must be a valid IANA timezone')
    .brand();
//# sourceMappingURL=iana-time-zone.js.map